// src/pages/po/Compras.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableList, faCartShopping, faClipboardList, faFileInvoice, faFolderOpen, faCircleCheck } from '@/icons';
import KanbanColumn from '../../components/common/KanbanColumn';
import KanbanCard from '../../components/common/KanbanCard';
import SidePanel from '../../components/common/SidePanel';
import RequisicaoItemsSidePanelContent from './RequisicaoItemsSidePanelContent';
import OrdemCompraItemsSidePanelContent from './OrdemCompraItemsSidePanelContent';
import PreOrdemItemsSidePanelContent from './PreOrdemItemsSidePanelContent';
import { useTheme } from '../../contexts/ThemeContext';
import { Compra, OrdemCompra, PreOrdem } from '../../types/compras/Compra';
import './Compras.css';
import { formatDateTime, formatDate, isDateOverdue } from '../../utils/formatUtils';
import IconSearchButton from '../../components/common/IconSearchButton';
import IconResetButton from '../../components/common/IconResetButton';
import IconExportButton from '../../components/common/IconExportButton';
import Modal from '../../components/common/Modal';
import AdvancedSearchPurchasesKanbanModalContent from './AdvancedSearchPurchasesKanbanModalContent';
import ExportExcelModalContent from './ExportExcelModalContent';
import { useAuth } from '../../contexts/AuthContext';
import { useComprasKanban } from '../../hooks/useComprasKanban';
import { useComprasMutations } from '../../hooks/useComprasMutations';

const formatEstimatedTotal = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `Total estimado: ${value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
  }
  return 'Sem histórico de compra';
};

const Compras = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const kanbans = useComprasKanban();
  const mutations = useComprasMutations({
    compras: kanbans.compras,
    preOrdens: kanbans.preOrdens,
    ordensAbertas: kanbans.ordensAbertas,
    ordensFinalizadas: kanbans.ordensFinalizadas,
  });
  const [showItemsPanel, setShowItemsPanel] = useState(false);
  const [selectedCompra, setSelectedCompra] = useState<Compra | null>(null);
  const [selectedOrdemCompra, setSelectedOrdemCompra] = useState<OrdemCompra | null>(null);
  const [selectedPreOrdem, setSelectedPreOrdem] = useState<PreOrdem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);

  const comprasSentinelRef = useRef<HTMLDivElement>(null);
  const preOrdensSentinelRef = useRef<HTMLDivElement>(null);
  const ordensAbertasSentinelRef = useRef<HTMLDivElement>(null);
  const ordensFinalizadasSentinelRef = useRef<HTMLDivElement>(null);
  const comprasScrolledRef = useRef(false);
  const preOrdensScrolledRef = useRef(false);
  const ordensAbertasScrolledRef = useRef(false);
  const ordensFinalizadasScrolledRef = useRef(false);

  useEffect(() => {
    document.title = 'Bluelt | Compras';
  }, []);

  useEffect(() => {
    const attachScrollGuard = (
      sentinelRef: React.RefObject<HTMLDivElement | null>,
      scrolledRef: React.MutableRefObject<boolean>
    ) => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return undefined;
      const column = sentinel.closest('.kanban-column');
      const scrollEl = column?.querySelector('.kanban-column-content');
      if (!scrollEl) return undefined;

      const handleScroll = () => {
        if (scrollEl.scrollTop > 0) scrolledRef.current = true;
      };
      scrollEl.addEventListener('scroll', handleScroll);
      return () => scrollEl.removeEventListener('scroll', handleScroll);
    };

    const cleanupCompras = attachScrollGuard(comprasSentinelRef, comprasScrolledRef);
    const cleanupPreOrdens = attachScrollGuard(preOrdensSentinelRef, preOrdensScrolledRef);
    const cleanupOrdensAbertas = attachScrollGuard(ordensAbertasSentinelRef, ordensAbertasScrolledRef);
    const cleanupOrdensFinalizadas = attachScrollGuard(ordensFinalizadasSentinelRef, ordensFinalizadasScrolledRef);

    return () => {
      cleanupCompras?.();
      cleanupPreOrdens?.();
      cleanupOrdensAbertas?.();
      cleanupOrdensFinalizadas?.();
    };
  }, []);

  useEffect(() => {
    if (kanbans.compras.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.compras.isLoading) {
          if (kanbans.isSearchActive && !comprasScrolledRef.current) return;
          kanbans.compras.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = comprasSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.compras.items, kanbans.compras.isLoading, kanbans.compras.loadMore, kanbans.isSearchActive]);

  useEffect(() => {
    if (kanbans.preOrdens.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.preOrdens.isLoading) {
          if (kanbans.isSearchActive && !preOrdensScrolledRef.current) return;
          kanbans.preOrdens.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = preOrdensSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.preOrdens.items, kanbans.preOrdens.isLoading, kanbans.preOrdens.loadMore, kanbans.isSearchActive]);

  useEffect(() => {
    if (kanbans.ordensAbertas.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.ordensAbertas.isLoading) {
          if (kanbans.isSearchActive && !ordensAbertasScrolledRef.current) return;
          kanbans.ordensAbertas.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = ordensAbertasSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.ordensAbertas.items, kanbans.ordensAbertas.isLoading, kanbans.ordensAbertas.loadMore, kanbans.isSearchActive]);

  useEffect(() => {
    if (kanbans.ordensFinalizadas.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.ordensFinalizadas.isLoading) {
          if (kanbans.isSearchActive && !ordensFinalizadasScrolledRef.current) return;
          kanbans.ordensFinalizadas.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = ordensFinalizadasSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.ordensFinalizadas.items, kanbans.ordensFinalizadas.isLoading, kanbans.ordensFinalizadas.loadMore, kanbans.isSearchActive]);

  const handleOpenSearchModal = () => {
    setIsModalOpen(true);
  };

  const handleOpenExportModal = () => {
    setIsExportModalOpen(true);
  };

  // Wrapper: delega reset de search state ao hook + reseta scrolledRefs locais (DOM state)
  const handleResetSearch = () => {
    kanbans.handleResetSearch();
    comprasScrolledRef.current = false;
    preOrdensScrolledRef.current = false;
    ordensAbertasScrolledRef.current = false;
    ordensFinalizadasScrolledRef.current = false;
  };

  const handleViewCompraItemsClick = useCallback((compra: Compra) => {
    setSelectedCompra(compra);
    setShowItemsPanel(true);
  }, []);

  const handleViewOrdemCompraItemsClick = useCallback((ordem: OrdemCompra) => {
    setSelectedOrdemCompra(ordem);
    setShowItemsPanel(true);
  }, []);

  const handleViewPreOrdemItemsClick = useCallback((preOrdem: PreOrdem) => {
    setSelectedPreOrdem(preOrdem);
    setShowItemsPanel(true);
  }, []);

  const handleClosePanel = () => {
    setShowItemsPanel(false);
    setSelectedCompra(null);
    setSelectedOrdemCompra(null);
    setSelectedPreOrdem(null);
    if (dataChanged) {
      handleResetSearch();
      kanbans.reloadAllColumns();
      setDataChanged(false);
    }
  };

  const handleDataChange = () => {
    setDataChanged(true);
  };

  const renderCompraItem = useCallback((compra: Compra) => (
      <KanbanCard<Compra>
          item={compra}
          renderContent={(r) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <strong>Requisição {r.id}</strong>
              {r.prioridadeUrgente && (
                <strong>Requisição Urgente!</strong>
              )}
              {Object.entries(r.itensCount)
                .filter(([tipo]: [string, number]) => tipo === 'COMPRA')
                .map(([tipo, quantidade]: [string, number]) => (
                  <React.Fragment key={tipo}>
                    <div>Tipo: {tipo} | Quantidade itens: {quantidade}</div>
                    <div>
                      <strong>{formatEstimatedTotal(r.somaCustoTotalEstim)}</strong>
                    </div>
                  </React.Fragment>
                ))}
              <div>
                {!r.nroContato && r.origem === 'PLANEJAMENTO'
                  ? 'Solicitação Planejamento'
                  : `Contato ${r.nroContato}`} | Filial: {r.nomeFantasia}
              </div>
              <div>Requisitante: {r.nomeUsuario}</div>
              {r.origem !== 'PLANEJAMENTO' && (
                <div>Cliente: {r.nomeCliente}</div>
              )}
              {r.origem !== 'PLANEJAMENTO' && (
                <div>Consultor: {r.nomeUsuarioConsultor}</div>
              )}
              <div>
                {r.countDiasAberto === 0
                  ? 'Aberto hoje'
                  : r.countDiasAberto < 0
                  ? 'Dias em Aberto: Erro'
                  : `Aberto há ${r.countDiasAberto} dia${r.countDiasAberto === 1 ? '' : 's'}`}
              </div>
            </div>
          )}
          isDraggable={true}
          onDragStart={mutations.onDragStartPending}
          actionButton={{
              icon: faTableList,
              onClick: () => handleViewCompraItemsClick(compra),
              title: 'Visualizar Detalhes',
              className: 'list-itens-card list-itens-card-requisicoes-compra',
          }}
          className="compra-card"
          isValid={!compra.prioridadeUrgente}
      />
  ), [handleViewCompraItemsClick, mutations.onDragStartPending]);

  const renderPreOrdemItem = useCallback((preOrdem: PreOrdem) => (
    <KanbanCard<PreOrdem>
      item={preOrdem}
      renderContent={(p) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Cotação {p.id}</strong>
          <div>Quantidade itens: {p.quantidadeItens}</div>
          <div>Comprador(a): {p.nomeUsuario}</div>
          <div>Fornecedor: {p.nomeFornecedor}</div>
          <div>Loja de entrega: {p.lojaEntrega}</div>
          <div>
            {p.countDiasAberto === 0
              ? 'Aberto hoje'
              : p.countDiasAberto < 0
                ? 'Dias em Aberto: Erro'
                : `Aberto há ${p.countDiasAberto} dia${p.countDiasAberto === 1 ? '' : 's'}`}
          </div>
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartCotacoes}
      actionButton={{
        icon: faTableList,
        onClick: () => handleViewPreOrdemItemsClick(preOrdem),
        title: 'Visualizar Detalhes',
        className: 'list-itens-card list-itens-card-pre-ordens',
      }}
      className="pre-ordem-card"
    />
  ), [handleViewPreOrdemItemsClick, mutations.onDragStartCotacoes]);

  const renderOrdemCompraItem = useCallback((ordem: OrdemCompra) => (
      <KanbanCard<OrdemCompra>
          item={ordem}
          renderContent={(o) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <strong>Ordem de Compra {o.id}</strong>
                  <div>Quantidade itens: {o.countItens}</div>
                  <div>Comprador(a): {o.nomeUsuario}</div>
                  <div>Fornecedor: {o.nomeFornecedor || 'Erro'}</div>
                  <div>Loja de Entrega: {o.nomeLojaEntrega}</div>
                  {o.status === 'ABERTA' && (
                      <div>
                          Previsão de chegada: {formatDate(o.dataEntrega)}
                          {isDateOverdue(o.dataEntrega) && <strong> - EM ATRASO!</strong>}
                      </div>
                  )}
                  <div>
                      {o.status === 'FINALIZADO'
                          ? `Encerrada em: ${formatDateTime(o.dataEncerrada ?? '')}`
                          : o.countDiasAberto === 0
                          ? 'Aberto hoje'
                          : o.countDiasAberto < 0
                          ? 'Dias em Aberto: Erro'
                          : `Aberto há ${o.countDiasAberto} dia${o.countDiasAberto === 1 ? '' : 's'}`}
                  </div>
              </div>
          )}
          isDraggable={ordem.status !== 'CANCELADO' && ordem.status !== 'FINALIZADO'}
          onDragStart={(item) => (item.status === 'FINALIZADO' ? mutations.onDragStartFinalizada(item) : mutations.onDragStartAberta(item))}
          actionButton={{
              icon: faTableList,
              onClick: () => handleViewOrdemCompraItemsClick(ordem),
              title: 'Visualizar Detalhes',
              className: `list-itens-card list-itens-card-ordens-${ordem.status === 'FINALIZADO' ? 'finalizadas' : 'abertas'}`,
          }}
          className="ordem-card"
          isValid={!(ordem.status === 'ABERTA' && isDateOverdue(ordem.dataEntrega))} // Aplica invalid-card se atrasada e ABERTA
      />
  ), [handleViewOrdemCompraItemsClick, mutations.onDragStartFinalizada, mutations.onDragStartAberta]);

  const requiredPermission = 'modulo.gestao_compras';
  if (!hasPermission(requiredPermission)) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  return (
    <div className={`compras-page ${theme}`}>
      <main className="compras-main">
        <h1>
          <FontAwesomeIcon icon={faCartShopping} /> Gestão de Compras
        </h1>
        <div className="header-actions">
          <IconExportButton
            onClick={handleOpenExportModal}
            title="Exportar para Excel"
          />
          {kanbans.isSearchActive && (
            <IconResetButton
              onClick={handleResetSearch}
              title="Limpar Pesquisa"
            />
          )}
          <IconSearchButton
            onClick={handleOpenSearchModal}
            title="Pesquisar"
          />
        </div>
      </main>
      <div className="compras-content">
        <div className="kanban-container">
          <KanbanColumn<Compra>
            title="Requisições Pendentes"
            icon={faClipboardList}
            data={kanbans.compras.items}
            renderItem={renderCompraItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'pending')}
            ref={comprasSentinelRef}
            emptyMessage="Nenhuma requisição pendente encontrada"
            showCountCircle={true}
            totalCount={kanbans.compras.totalItems}
          />
          <KanbanColumn<PreOrdem>
            title="Cotações"
            icon={faFileInvoice}
            data={kanbans.preOrdens.items}
            renderItem={renderPreOrdemItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'cotacoes')}
            ref={preOrdensSentinelRef}
            emptyMessage="Nenhuma cotação encontrada"
            showCountCircle={true}
            totalCount={kanbans.preOrdens.totalItems}
          />
          <KanbanColumn<OrdemCompra>
            title="Ordens de Compra Abertas"
            icon={faFolderOpen}
            data={kanbans.ordensAbertas.items}
            renderItem={renderOrdemCompraItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'aberta')}
            ref={ordensAbertasSentinelRef}
            emptyMessage="Nenhuma ordem de compra aberta"
            showCountCircle={true}
            totalCount={kanbans.ordensAbertas.totalItems}
          />
          <KanbanColumn<OrdemCompra>
            title="Ordens de Compra Finalizadas"
            icon={faCircleCheck}
            data={kanbans.ordensFinalizadas.items}
            renderItem={renderOrdemCompraItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'finalizada')}
            ref={ordensFinalizadasSentinelRef}
            emptyMessage="Nenhuma ordem de compra finalizada"
            showCountCircle={true}
            totalCount={kanbans.ordensFinalizadas.totalItems}
          />
        </div>
        <SidePanel isOpen={showItemsPanel} onClose={handleClosePanel} theme={theme}>
          {selectedCompra && (
            <RequisicaoItemsSidePanelContent
              requisicao={selectedCompra}
              theme={theme}
              onClose={handleClosePanel}
              onDataChange={handleDataChange}
              tipoRequisicao="COMPRA"
              showPurchaseHistoryAction={true}
            />
          )}
          {selectedOrdemCompra && (
            <OrdemCompraItemsSidePanelContent
              ordemCompra={selectedOrdemCompra}
              theme={theme}
              onClose={handleClosePanel}
              onDataChange={handleDataChange}
            />
          )}
          {selectedPreOrdem && (
            <PreOrdemItemsSidePanelContent
              preOrdem={selectedPreOrdem}
              theme={theme}
              onClose={handleClosePanel}
              onDataChange={handleDataChange}
              showCheckboxesInitially={true}
            />
          )}
        </SidePanel>
      </div>
      <Modal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} theme={theme}>
        <ExportExcelModalContent
          theme={theme}
          onClose={() => setIsExportModalOpen(false)}
          allIdsAbertas={kanbans.allIdsAbertas}
          allIdsFinalizadas={kanbans.allIdsFinalizadas}
        />
      </Modal>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} theme={theme}>
        <AdvancedSearchPurchasesKanbanModalContent
          theme={theme}
          onClose={() => setIsModalOpen(false)}
          onSearch={(result) => {
            kanbans.handleSearch(result.__query || '');
            comprasScrolledRef.current = false;
            preOrdensScrolledRef.current = false;
            ordensAbertasScrolledRef.current = false;
            ordensFinalizadasScrolledRef.current = false;
          }}
        />
      </Modal>
    </div>
  );
};

export default Compras;
